import { OrgContext } from "contexts/OrgContext";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ComputeSelector from "./ComputeSelector";
import ContainerSelector from "./ContainerSelector";
import "./style.scss";
import { classNames, validateName } from "components/utils";
import {
  ArrowUpRightIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";

import InlineNotification from "contexts/Notifications/InlineNotifications";

import { Badge } from "components/Graphs/Badge";
import FilterBar from "./ComputeSelector/Advanced/FilterBar";
import { ContainerSelected, parseDockerComposeUrl } from "../shared/BuildTypes";
import { GPUInstanceType } from "../Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import { allPublicCloudsName } from "./utils";
import { NameEnvironment } from "./EnvironmentCreate";
import { Button } from "components/Graphs/Button";
import agent from "server";
import { useQuery } from "@tanstack/react-query";
import UnauthenticatedDash from "components/DashboardContainer/UnauthenticatedDash";
import { useHistory } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { useKas } from "contexts/KasContext";
import { isKasAuthFlow, redirectUserToLogin } from "server/kas/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";

export interface EnvironmentCreateProps {}

export const filterForGPUInstances = (
  instances: GPUInstanceType[],
  includeShadeform: boolean
) => {
  return instances
    .filter((gpu) => gpu.supported_gpus)
    .filter((gpu) => gpu.supported_gpus[0].manufacturer !== "Habana")
    .filter((gpu) => gpu.supported_gpus[0].manufacturer !== "AMD")
    .filter((gpu) => (includeShadeform ? true : gpu.provider !== "shadeform"))
    .map((gpu) => ({
      ...gpu,
      supported_gpus: [
        {
          ...gpu.supported_gpus[0],
          name: formatGPUName(gpu.supported_gpus[0].name),
        },
      ] as [(typeof gpu.supported_gpus)[0]],
    }));
};

export const formatGPUName = (name: string): string => {
  let formattedName = name;

  // Only process if the name contains RTX, Pro, or Ada
  if (
    formattedName.includes("RTX") ||
    formattedName.includes("Pro") ||
    formattedName.includes("Ada")
  ) {
    // Add space after RTX if not already spaced
    formattedName = formattedName.replace(/RTX(?=[A-Za-z0-9])/g, "RTX ");

    // Add spaces around Pro if not already spaced
    formattedName = formattedName.replace(
      /([A-Za-z])Pro(?=[A-Za-z0-9])/g,
      "$1 Pro "
    );
    formattedName = formattedName.replace(/Pro([A-Za-z0-9])/g, "Pro $1");

    // Add spaces around Ada if not already spaced
    formattedName = formattedName.replace(
      /([A-Za-z0-9])Ada(?=[A-Za-z0-9])/g,
      "$1 Ada "
    );
    formattedName = formattedName.replace(/([A-Za-z0-9])Ada$/g, "$1 Ada");
    formattedName = formattedName.replace(/Ada([A-Za-z0-9])/g, "Ada $1");

    // Add space between letters and numbers for these special cases only
    formattedName = formattedName.replace(/([A-Za-z])([0-9])/g, "$1 $2");
  }

  // Clean up multiple consecutive spaces
  formattedName = formattedName.replace(/\s+/g, " ").trim();

  return formattedName;
};

const EnvironmentCreatePublic: React.FC = () => {
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const instanceFromQuery = queryParams.get("instanceType");

  const [container, setContainer] = useState<ContainerSelected>({
    vmBuild: {
      forceJupyterInstall: false,
    },
  });
  const [pythonVersion, setPythonVersion] = useState<string>("3.10");
  const [cudaVersion, setCudaVersion] = useState<string>("12.0.1");

  const includeShadeform = useFeatureFlagEnabled("include-shadeform");

  const [environmentName, setEnvironmentName] =
    useState<string>("awesome-gpu-name");
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");
  const [selectedInstanceType, setSelectedInstanceType] =
    useState<GPUInstanceType>();
  const [storage, setStorage] = useState<string>("256");
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);
  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[]
  >([]);
  const [allGPUInstances, setAllGPUInstances] = useState<GPUInstanceType[]>([]);

  const {
    isAuthenticated: _auth0IsAuthenticated,
    user: _auth0User,
    isLoading: _auth0IsLoading,
  } = useAuth0?.();

  const {
    isUserLoggedIn: kasIsAuthenticated,
    user: kasUser,
    isLoading: kasIsLoading,
  } = useKas();

  const auth0IsAuthenticated = isKasAuthFlow
    ? kasIsAuthenticated
    : _auth0IsAuthenticated;
  const auth0User = isKasAuthFlow ? kasUser : _auth0User;
  const auth0IsLoading = isKasAuthFlow ? kasIsLoading : _auth0IsLoading;

  const getAllPublicInstanceTypes = async () => {
    const res = await agent.Instances.getAllPublicInstanceTypes();
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch workspaces");
    }
    return res.data;
  };

  const { data: instancesWithErrors, isLoading: isInstancesLoading } = useQuery(
    {
      queryKey: ["instances-with-errors"],
      queryFn: () => getAllPublicInstanceTypes(),
    }
  );

  useEffect(() => {
    if (instancesWithErrors) {
      const gpuInstances = filterForGPUInstances(
        instancesWithErrors.allInstanceTypes,
        includeShadeform ?? false
      ).filter(
        (instance) =>
          !instance.supported_gpus[0].name.includes("5090") &&
          !instance.supported_gpus[0].name.includes("4090")
      );
      setAllGPUInstances(gpuInstances);
      setDisplayGPUInstances(gpuInstances);
    }
  }, [instancesWithErrors, includeShadeform]);

  useEffect(() => {
    if (auth0IsLoading) {
      return;
    }
    if (isKasAuthFlow && auth0IsAuthenticated && auth0User) {
      history.push(`/environment/new?${queryParams.toString()}`);
    }
  }, [
    auth0IsAuthenticated,
    auth0User,
    isKasAuthFlow,
    auth0IsLoading,
    queryParams,
  ]);

  const handleNameChange = (val: string) => {
    setEnvironmentName(val);

    // Set validation error messaging
    if (val === "") {
      setNameFieldErrorMessage("Name required!");
      return;
    }
    const isValid = validateName(val);
    if (!isValid) {
      setNameFieldErrorMessage(
        "Name can only have letters, numbers and dashes."
      );
      return;
    }
    setNameFieldErrorMessage("");
  };

  useEffect(() => {
    if (instanceFromQuery) {
      const found = allGPUInstances.find((i) => i.type === instanceFromQuery);
      if (found) {
        setSelectedInstanceType(found);
      } else {
        console.error("Instance type not found");
      }
    }
  }, [instanceFromQuery, allGPUInstances]);

  return (
    <UnauthenticatedDash>
      <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-10 pt-3">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between mt-8">
            <div className="flex mb-2">
              <h1 className="text-xl  sm:text-2xl font-bold text-gray-900 dark:text-slate-100">
                {!container && "Select your Container"}
                {container && !selectedInstanceType && "Select your Compute"}
                {container && !!selectedInstanceType && "Configure and Deploy"}
              </h1>
            </div>
            <div className="flex-row items-end hidden sm:flex">
              {container && !selectedInstanceType && (
                <FilterBar
                  allGPUInstances={allGPUInstances}
                  setDisplayGPUInstances={setDisplayGPUInstances}
                  selectedCloud={selectedCloudName}
                  setSelectedCloud={setSelectedCloudName}
                  containerSelected={container}
                  displayGPUInstances={displayGPUInstances}
                />
              )}
            </div>
          </div>
          <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
          <div className="flex flex-col">
            <AnimatePresence initial={false}>
              {!container && (
                <motion.section
                  key="container"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { opacity: 1, height: "auto" },
                    collapsed: { opacity: 0, height: 0 },
                  }}
                  transition={{
                    duration: 0.8,
                    ease: [0.04, 0.62, 0.23, 0.98],
                  }}
                >
                  <motion.div
                    variants={{
                      collapsed: { scale: 0.8 },
                      open: { scale: 1 },
                    }}
                    transition={{ duration: 0.8 }}
                    className="content-placeholder"
                  >
                    <ContainerSelector
                      hideTitle={true}
                      containerSelected={container}
                      onSelectContainer={setContainer}
                      pythonVersion={pythonVersion}
                      setPythonVersion={setPythonVersion}
                      cudaVersion={cudaVersion}
                      setCudaVersion={setCudaVersion}
                    />
                  </motion.div>
                </motion.section>
              )}
              {container && (
                <>
                  <ComputeSelector
                    allGPUInstances={filterForGPUInstances(
                      instancesWithErrors?.allInstanceTypes || [],
                      includeShadeform ?? false
                    )}
                    isLoadingInstances={isInstancesLoading}
                    displayGPUInstances={displayGPUInstances}
                    setDisplayGPUInstances={setDisplayGPUInstances}
                    selectedInstance={selectedInstanceType}
                    setSelectedInstance={setSelectedInstanceType}
                    setStorage={setStorage}
                    storage={storage}
                    containerSelected={container}
                    selectedCloudName={selectedCloudName}
                    setSelectedCloudName={setSelectedCloudName}
                  />
                  {!!selectedInstanceType && (
                    <div className="flex flex-col mt-10">
                      <div>
                        <Button
                          onClick={() => {
                            // window.open(
                            //   `${
                            //     window.location.origin
                            //   }/environment/new?instanceType=${
                            //     selectedInstanceType.type
                            //   }&storage=${
                            //     selectedInstanceType.elastic_root_volume
                            //       ? storage
                            //       : ""
                            //   }`
                            // );

                            localStorage.setItem(
                              "brev-redirect-url",
                              `/environment/new?instanceType=${
                                selectedInstanceType.type
                              }&storage=${
                                selectedInstanceType.elastic_root_volume
                                  ? storage
                                  : ""
                              }`
                            );
                            redirectUserToLogin();
                          }}
                        >
                          <span>Deploy</span>
                          <ArrowUpRightIcon className="ml-1 w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </UnauthenticatedDash>
  );
};

export default EnvironmentCreatePublic;
